<script lang="ts" setup>
import { useLayouts } from "@layouts";
import { config } from "@layouts/config";
import { can } from "@layouts/plugins/casl";
import type { NavLink } from "@layouts/types";
import { getComputedNavLinkToProp, isNavLinkActive } from "@layouts/utils";
import { useRouter } from "vue-router";

const props = defineProps<{
  item: NavLink;
}>();

const { width: windowWidth } = useWindowSize();
const { isVerticalNavMini, dynamicI18nProps } = useLayouts();
const router = useRouter();
const hideTitleAndBadge = isVerticalNavMini(windowWidth);

const useExternalURL = (item:any) => {
  return typeof item.to === 'string' && item.to.startsWith('http');
}

const handleNavClick = (event?: MouseEvent) => {
  if (props.item.to?.name === "qb") {
    if (router.currentRoute.value.path !== "/qb") {
      router.push("/qb");
    }
    window.dispatchEvent(
      new CustomEvent("toggleQuickBooking", { detail: true })
    );
  } else if (props.item.onClick) {
    event?.preventDefault();
    props.item.onClick();
  }
};
</script>

<template>
  <li
    v-if="can(item.action, item.subject)"
    class="nav-link"
    :class="{ disabled: item.disable }"
  >
    <Component
      :is="useExternalURL(item) ? 'a' : item.to ? 'RouterLink' : 'a'"
      v-bind="
        useExternalURL(item) ? {
              href: item.to,
              rel: 'noopener noreferrer',
            }
          : getComputedNavLinkToProp(item)
      "
      @click="handleNavClick"
      :class="{
        'router-link-active router-link-exact-active': isNavLinkActive(
          item,
          $router
        ),
      }"
    >
      <Component
        :is="config.app.iconRenderer || 'div'"
        v-bind="item.icon || config.verticalNav.defaultNavItemIconProps"
        class="nav-item-icon"
      />
      <TransitionGroup name="transition-slide-x">
        <!-- 👉 Title -->
        <Component
          :is="config.app.enableI18n ? 'i18n-t' : 'span'"
          v-show="!hideTitleAndBadge"
          key="title"
          class="nav-item-title"
          v-bind="dynamicI18nProps(item.title, 'span')"
        >
          {{ item.title }}
        </Component>

        <!-- 👉 Badge -->
        <Component
          :is="config.app.enableI18n ? 'i18n-t' : 'span'"
          v-if="item.badgeContent"
          v-show="!hideTitleAndBadge"
          key="badge"
          class="nav-item-badge"
          :class="item.badgeClass"
          v-bind="dynamicI18nProps(item.badgeContent, 'span')"
        >
          {{ item.badgeContent }}
        </Component>
      </TransitionGroup>
    </Component>
  </li>
</template>

<style lang="scss">
.layout-vertical-nav {
  .nav-link a {
    display: flex;
    align-items: center;
  }
}
</style>
